import { img, CARS } from "./images";
import {
  FaCity,
  FaPlane,
  FaBriefcase,
  FaCrown,
  FaUsers,
  FaBus,
} from "react-icons/fa";

/* ---------------- SERVICES ---------------- */
export const services = [
  {
    id: "city-rides",
    icon: FaCity,
    title: "City Rides",
    short:
      "Reliable and convenient rides within the city. Perfect for commuting, errands, and local events.",
    image: CARS.saloon,
    desc: "Get around town quickly and comfortably with our dependable city ride service. Whether it's a daily commute, a quick errand or a night out, our professional drivers get you there safely and on time.",
    benefits: [
      "Professional and experienced drivers",
      "Affordable and transparent pricing",
      "Comfortable and clean vehicles",
      "Fast and reliable transportation",
    ],
    process: [
      "Enter your pickup and drop-off locations",
      "Select your preferred ride type",
      "Confirm the fare and book your ride",
      "Track your ride in real-time",
      "Arrive safely and enjoy your trip",
    ],
    faqs: [
      {
        q: "How do I book a city ride?",
        a: "You can book a city ride through our website or mobile app. Simply enter your pickup and drop-off locations, select your ride type, and confirm your booking.",
      },
      {
        q: "Are your taxi fares fixed?",
        a: "We offer transparent, upfront pricing so you always know the fare before you confirm your booking.",
      },
    ],
  },
  {
    id: "airport-transfers",
    icon: FaPlane,
    title: "Airport Transfers",
    short:
      "Stress-free airport pickups and drop-offs with real-time flight tracking and on-time service.",
    image: CARS.estate,
    desc: "Never miss a flight again. Our airport transfer service monitors your flight in real-time and adjusts pickup so a professional driver is always ready and waiting when you land.",
    benefits: [
      "Comfortable and well-maintained vehicles",
      "Real-time flight tracking for timely pickups",
      "Available 24/7 for your convenience",
      "Fixed, transparent pricing",
    ],
    process: [
      "Easy online booking or phone reservation",
      "Flight tracking to adjust for delays",
      "Professional driver assigned for pickup",
      "Comfortable and safe ride to your destination",
      "Drop-off at the designated location on time",
    ],
    faqs: [
      {
        q: "How do I book an airport transfer?",
        a: "You can book online through our website or call our customer service for assistance. We recommend booking in advance for the best availability.",
      },
      {
        q: "What happens if my flight is delayed?",
        a: "We track flight schedules and adjust pickup times accordingly, ensuring that you have a driver waiting when you land.",
      },
    ],
  },
  {
    id: "corporate-rides",
    icon: FaBriefcase,
    title: "Corporate Rides",
    short:
      "Professional and efficient rides tailored for business meetings, conferences, and corporate events.",
    image: CARS.executive,
    desc: "Make the right impression with our corporate travel solutions. Punctual chauffeurs, premium vehicles and flexible billing make business travel seamless for teams of all sizes.",
    benefits: [
      "Professional chauffeurs ensuring punctuality",
      "On-demand or scheduled bookings",
      "Secure and confidential travel",
      "Flexible plans for businesses of all sizes",
    ],
    process: [
      "Easy online booking and scheduling",
      "Customized corporate travel plans",
      "Real-time tracking and updates",
      "Premium customer support",
      "Seamless invoicing and payment options",
    ],
    faqs: [
      {
        q: "Can I set up a corporate account?",
        a: "Yes, we offer corporate accounts with flexible billing and priority services. Contact us for details.",
      },
      {
        q: "Do you provide long-distance corporate rides?",
        a: "Absolutely! Our corporate rides cover both short and long-distance trips to meet your business needs.",
      },
    ],
  },
  {
    id: "luxury-rides",
    icon: FaCrown,
    title: "Luxury Rides",
    short:
      "Experience top-of-the-line vehicles and premium services for a sophisticated and comfortable ride.",
    image: CARS.standard,
    desc: "Enjoy plush interiors, complimentary refreshments, and top-tier service, ensuring a seamless and relaxing journey every time you book with us.",
    benefits: [
      "High-end vehicles with premium interiors",
      "Professional and courteous chauffeurs",
      "Complimentary refreshments and Wi-Fi",
      "Perfect for special occasions and business travel",
      "Reliable and punctual service",
    ],
    process: [
      "Effortless booking through our website or app",
      "Selection of premium vehicles to suit your needs",
      "On-time pickup and drop-off with professional service",
      "Luxurious and comfortable journey with added amenities",
      "Final quality check to ensure complete satisfaction",
    ],
    faqs: [
      {
        q: "What types of luxury vehicles do you offer?",
        a: "We provide a range of high-end vehicles, including limousines, executive sedans, and luxury SUVs, to match your preferences and needs.",
      },
      {
        q: "Can I request a specific chauffeur?",
        a: "Yes, we offer personalized chauffeur selection for frequent clients to ensure a consistent and familiar service experience.",
      },
    ],
  },
  {
    id: "group-rides",
    icon: FaUsers,
    title: "Group Rides",
    short:
      "Spacious vehicles for groups and events. Perfect for family outings, parties, and large gatherings.",
    image: CARS.minibus,
    desc: "Travelling as a group? Our spacious vans and minibuses keep everyone together and comfortable, with affordable cost-sharing options for any occasion.",
    benefits: [
      "Affordable rates with cost-sharing options",
      "Perfect for events, corporate travel, and family trips",
      "Easy booking and real-time tracking",
      "Spacious and comfortable vehicles",
    ],
    process: [
      "Simple online booking for groups of any size",
      "Selection of spacious vans, minibuses, or SUVs",
      "On-time pickup from multiple locations if needed",
      "Comfortable and enjoyable ride with ample space",
      "Safe and timely arrival at your destination",
    ],
    faqs: [
      {
        q: "How many passengers can a group ride accommodate?",
        a: "We offer different vehicle sizes, ranging from SUVs for small groups to minibuses for larger parties. Contact us to find the right fit for your group.",
      },
      {
        q: "Can we book multiple pickup locations?",
        a: "Yes, our group rides support multiple pickup points so the whole group can travel together comfortably.",
      },
    ],
  },
  {
    id: "shuttle-services",
    icon: FaBus,
    title: "Shuttle Services",
    short:
      "Scheduled and on-demand shuttle options ideal for corporate, airport, and event transportation.",
    image: CARS.mpv,
    desc: "With professional drivers and well-maintained vehicles, we ensure a safe, punctual, and comfortable travel experience, making your journey stress-free.",
    benefits: [
      "Scheduled and on-demand shuttle options",
      "Comfortable seating and climate control",
      "Ideal for corporate, airport, and event transportation",
      "Affordable pricing with multiple route options",
      "Real-time tracking and easy booking",
    ],
    process: [
      "Book your shuttle service online or via our app",
      "Choose from daily, weekly, or event-based routes",
      "Enjoy comfortable and hassle-free travel",
      "Real-time tracking to monitor shuttle arrival times",
      "Reliable drop-off at your designated destination",
    ],
    faqs: [
      {
        q: "Do you offer airport shuttle services?",
        a: "Yes, we provide airport shuttle services with flexible scheduling to ensure you arrive on time for your flight.",
      },
      {
        q: "Are your shuttle services available 24/7?",
        a: "Some of our shuttle routes operate 24/7, while others follow a set schedule. Please check availability for your specific route.",
      },
    ],
  },
];

/* ---------------- TAXIS / FLEET ---------------- */
export const taxis = [
  {
    id: "saloon-car",
    title: "Saloon Car",
    image: CARS.saloon,
    seats: 4,
    luggage: 2,
    desc: "Comfortable standard saloon, ideal for everyday journeys and up to 4 passengers with hand luggage.",
    models: ["Toyota Prius", "Volkswagen Passat", "Skoda Octavia", "Ford Mondeo", "Hyundai Ioniq"],
    features: [
      "Up to 4 Passengers",
      "2 Suitcases",
      "Air Conditioning",
      "Clean & Comfortable",
      "Friendly Local Drivers",
    ],
    reviews: [
      { name: "Hedley Howarth", text: "Great service! The ride was smooth and the driver was very friendly. Highly recommended for city trips." },
      { name: "Katrina Molloy", text: "Affordable and convenient. The saloon car was spotless and arrived right on time." },
    ],
  },
  {
    id: "estate-car",
    title: "Estate Car",
    image: CARS.estate,
    seats: 4,
    luggage: 4,
    desc: "Extra boot space for those with more luggage — perfect for airport runs and family trips.",
    models: ["Skoda Superb Estate", "VW Passat Estate", "Ford Focus Estate", "Mercedes C-Class Estate"],
    features: [
      "Up to 4 Passengers",
      "4 Suitcases",
      "Spacious Boot",
      "Ideal for Airports",
      "Comfortable Seating",
    ],
    reviews: [
      { name: "Michael Carter", text: "Loads of luggage room. Made our airport transfer completely stress-free." },
      { name: "Emma Roberts", text: "Perfect for our family trip with plenty of bags. Smooth and reliable." },
    ],
  },
  {
    id: "executive-car",
    title: "Executive Car",
    image: CARS.executive,
    seats: 4,
    luggage: 3,
    desc: "Premium business class travel with professional chauffeurs and top-of-the-range vehicles.",
    models: ["Mercedes E-Class", "BMW 5 Series", "Audi A6", "Jaguar XF"],
    features: [
      "Up to 4 Passengers",
      "3 Suitcases",
      "Professional Chauffeur",
      "Premium Comfort",
      "Business & Corporate Travel",
    ],
    reviews: [
      { name: "James Whitmore", text: "An amazing experience! The car was spotless and the driver was extremely professional. Perfect for business trips." },
      { name: "Sophia Bennett", text: "Truly premium service. The ride felt luxurious and the driver was courteous throughout." },
    ],
  },
  {
    id: "mpv",
    title: "MPV",
    image: CARS.mpv,
    seats: 6,
    luggage: 4,
    desc: "Multi-purpose vehicle with room for up to 6 passengers and extra luggage — great for groups.",
    models: ["Ford Galaxy", "VW Sharan", "Seat Alhambra", "Mercedes V-Class"],
    features: [
      "Up to 6 Passengers",
      "4 Suitcases",
      "Spacious Interior",
      "Ideal for Groups",
      "Comfortable Family Travel",
    ],
    reviews: [
      { name: "Daniel Reed", text: "Plenty of space for the whole family and our bags. Very comfortable ride." },
      { name: "Emily Watson", text: "Great option for group travel. Roomy, clean and on time." },
    ],
  },
  {
    id: "8-seater-minibus",
    title: "8-Seater Minibus",
    image: CARS.minibus,
    seats: 8,
    luggage: 8,
    desc: "Travel together with up to 8 passengers — perfect for large groups, events and airport transfers.",
    models: ["Mercedes Vito", "Ford Transit", "VW Transporter", "Renault Trafic"],
    features: [
      "Up to 8 Passengers",
      "8 Suitcases",
      "Large Group Travel",
      "Plenty of Luggage Space",
      "Events & Airport Runs",
    ],
    reviews: [
      { name: "Oliver Hughes", text: "Perfect for our group trip. Loads of space and a very smooth journey." },
      { name: "Grace Turner", text: "Booked for an event with friends — comfortable, spacious and reliable." },
    ],
  },
  {
    id: "wheelchair-accessible",
    title: "Wheelchair Accessible",
    image: CARS.wheelchair,
    seats: 4,
    luggage: 2,
    desc: "Specially adapted vehicles with ramp access for safe, comfortable wheelchair travel.",
    models: ["Mercedes Vito WAV", "VW Caddy WAV", "Peugeot Partner WAV"],
    features: [
      "Wheelchair Ramp Access",
      "Safe & Secure Travel",
      "Trained Drivers",
      "Comfortable Journey",
      "Accessible for Everyone",
    ],
    reviews: [
      { name: "Margaret Hall", text: "So helpful and considerate. The ramp made everything easy and the driver was wonderful." },
      { name: "Peter Collins", text: "Excellent accessible service. Safe, comfortable and very professional." },
    ],
  },
];

/* ---------------- TEAM ---------------- */
export const team = [
  {
    id: "liam-carter",
    name: "Liam Carter",
    role: "Senior Driver",
    image: img("1500648767791-00dcc994a43e", 600, 700),
    bio: "Over 10 years of accident-free driving experience and a passion for exceptional customer service.",
    highlights: [
      "Highly rated by passengers for punctuality and safety",
      "Expert in traffic navigation and route planning",
      "Trained in advanced driving techniques",
      "Provides excellent customer service and assistance",
    ],
    skills: [
      { label: "Defensive Driving", value: 95 },
      { label: "City Navigation", value: 90 },
      { label: "Customer Service", value: 88 },
    ],
  },
  {
    id: "avery-morgan",
    name: "Avery Morgan",
    role: "Operations Coordinator",
    image: img("1544005313-94ddf0286df2", 600, 700),
    bio: "Keeps the fleet running like clockwork with smart scheduling and dispatch optimization.",
    highlights: [
      "Reduced wait times with effective scheduling strategies",
      "Implemented customer feedback improvements",
      "Managed fleet maintenance and availability",
      "Enhanced driver communication and coordination",
    ],
    skills: [
      { label: "Fleet Management", value: 92 },
      { label: "Dispatch Coordination", value: 90 },
      { label: "Communication Skills", value: 85 },
    ],
  },
  {
    id: "sophie-allen",
    name: "Sophie Allen",
    role: "Customer Relations",
    image: img("1573497019940-1c28c88b4f3e", 600, 700),
    bio: "Builds lasting relationships with riders and resolves every query with a smile.",
    highlights: [
      "Improved customer satisfaction ratings by 30%",
      "Developed streamlined booking and support processes",
      "Trained staff in effective communication techniques",
      "Built strong relationships with regular customers",
    ],
    skills: [
      { label: "Communication Skills", value: 94 },
      { label: "Problem Solving", value: 89 },
      { label: "Customer Service", value: 96 },
    ],
  },
  {
    id: "noah-bennett",
    name: "Noah Bennett",
    role: "Senior Driver",
    image: img("1507003211169-0a1dd7228f2d", 600, 700),
    bio: "Top-rated driver committed to ensuring passenger safety and satisfaction on every trip.",
    highlights: [
      "Successfully completed thousands of safe trips",
      "Expert in city navigation and traffic management",
      "Top-rated driver with outstanding customer feedback",
      "Trained in advanced defensive driving techniques",
    ],
    skills: [
      { label: "Safe Driving", value: 97 },
      { label: "Navigation Skills", value: 91 },
      { label: "Customer Service", value: 87 },
    ],
  },
];

/* ---------------- STATS ---------------- */
export const stats = [
  { value: 15, suffix: "K+", label: "Happy Customers" },
  { value: 320, suffix: "+", label: "Professional Drivers" },
  { value: 50, suffix: "+", label: "Cities Covered" },
  { value: 98, suffix: "%", label: "Satisfaction Rate" },
];

/* ---------------- WHY CHOOSE / FEATURES ---------------- */
export const features = [
  {
    title: "Affordable Rates",
    text: "Transparent, competitive pricing with no hidden charges on any ride.",
    icon: "💰",
  },
  {
    title: "Experienced Drivers",
    text: "Licensed, professional drivers trained for safety and comfort.",
    icon: "🧑‍✈️",
  },
  {
    title: "Well-Maintained Vehicles",
    text: "A clean, modern fleet serviced regularly for a smooth ride.",
    icon: "🚖",
  },
  {
    title: "24/7 Availability",
    text: "Book a ride any time, day or night, whenever you need us.",
    icon: "🕒",
  },
  {
    title: "Easy Booking Process",
    text: "Reserve your ride in seconds through our website or app.",
    icon: "📱",
  },
  {
    title: "Flexible Payment Options",
    text: "Pay by card, cash or digital wallet — whatever suits you.",
    icon: "💳",
  },
];

/* ---------------- WORK PROCESS ---------------- */
export const workProcess = [
  { step: "01", title: "Book online Now", text: "Book your taxi in seconds on our website or call us — choose your pickup, destination and vehicle." },
  { step: "02", title: "Get Instant Confirmation", text: "Receive quick confirmation with your fare and driver details, available 24/7 across London." },
  { step: "03", title: "Meet Your Driver", text: "Our licensed, professional driver arrives on time in a clean, comfortable vehicle." },
  { step: "04", title: "Enjoy Your Ride", text: "Relax and travel safely to your destination, then pay easily by card or cash." },
];

/* ---------------- PRICING ---------------- */
/* ---------------- FAQ ---------------- */
export const faqs = [
  { q: "How do I book a ride with Taxi24hrs?", a: "Simply enter your pickup and drop-off locations on our website or app, choose a vehicle type, and confirm your booking. You'll be matched with the nearest driver instantly." },
  { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards, cash, and popular digital wallets for a flexible and convenient payment experience." },
  { q: "Can I schedule a ride in advance?", a: "Yes! You can schedule rides hours or days in advance — perfect for airport transfers and important appointments." },
  { q: "Are your drivers professionally trained?", a: "All Taxi24hrs drivers are licensed professionals who undergo background checks and advanced safety training." },
  { q: "Is there a cancellation fee?", a: "Cancellations made within the free window incur no charge. A small fee may apply for late cancellations." },
  { q: "Do you operate 24/7?", a: "Yes, our service is available around the clock, every day of the year, so you can book a ride whenever you need one." },
];

/* ---------------- TESTIMONIALS ---------------- */
export const testimonials = [
  { name: "James Carter", role: "Frequent Rider", image: img("1507003211169-0a1dd7228f2d", 200, 200), rating: 5, text: "This is great news! I always appreciate a clean and modern taxi experience. Looking forward to trying out the new fleet." },
  { name: "Sophia Lewis", role: "Business Traveler", image: img("1573497019940-1c28c88b4f3e", 200, 200), rating: 5, text: "Absolutely! A comfortable ride makes a big difference, especially for long journeys. Taxi24hrs never disappoints." },
  { name: "Liam Johnson", role: "Daily Commuter", image: img("1500648767791-00dcc994a43e", 200, 200), rating: 5, text: "I love the focus on eco-friendly vehicles. Sustainable travel is the way forward and Taxi24hrs leads the way!" },
  { name: "Emily Brown", role: "Traveler", image: img("1544005313-94ddf0286df2", 200, 200), rating: 5, text: "Great service! I always struggle with airport transfers, so the real-time flight tracking is really helpful." },
  { name: "Michael Roberts", role: "Entrepreneur", image: img("1463453091185-61582044d556", 200, 200), rating: 5, text: "It's great to see the taxi industry embracing technology and sustainability. The app makes booking effortless." },
  { name: "Sarah Green", role: "Student", image: img("1438761681033-6461ffad8d80", 200, 200), rating: 5, text: "Affordable, safe and always on time. The shared ride option saves me so much on my daily commute." },
];

/* ---------------- PARTNERS ---------------- */
export const partners = [
  "Innovate Solutions",
  "Swift Transit",
  "SafeRide",
  "EliteRide",
  "Metro Logistics",
  "UrbanGo",
];

/* ---------------- GALLERY ---------------- */
export const gallery = [
  CARS.saloon,
  CARS.estate,
  CARS.executive,
  CARS.mpv,
  CARS.minibus,
  CARS.wheelchair,
  CARS.standard,
  CARS.executive,
  CARS.saloon,
];
